/**
 * Module: WalletScreen
 * Role: Копилка screen — balance display + idempotent withdraw flow (FTR-1005 Task 5).
 *       History list wired to /earnings/history?limit=20 (FTR-1032 Task 3).
 *       Shared caches: ['earnings','balance'] (WalletCard), ['buyer','profile'] (ProfileScreen).
 * Uses:
 *   - atoms, @tanstack/react-query, apiFetch, useNavigate, P/S/T, formatKopecks
 *   - ./WalletScreen/HistorySection (HistorySection component + EarningsHistoryItem type)
 * Used by: screens/AppShell (routed at /wallet)
 * Glossary: ai/glossary/buyer.md
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { IOSScreen, PageHeader, Card, SectionHeader, EmptyState, Skeleton, PressBtn, Ic } from '../components/atoms';
import { P, S, T } from '../styles/tokens';
import { formatKopecks } from '../lib/format';
import { HistorySection, type EarningsHistoryItem } from './WalletScreen/HistorySection';

// ─── Backend contracts ────────────────────────────────────────────────────────
interface BalanceView {
  available_kopecks: number;
  pending_kopecks: number;
  accrued_total_kopecks: number;
  withdrawn_total_kopecks: number;
  currency: 'RUB';
  calculated_at: string;
}
interface ProfileView {
  sbp_complete: boolean;
  sbp_phone_masked?: string | null;
  sbp_bank_name?: string | null;
  fio_display?: string | null;
  [key: string]: unknown;
}
interface WithdrawInitResponse { withdrawal_id: string; status: string; }
interface WithdrawStatusResponse { withdrawal_id: string; status: string; reason?: string | null; }
interface EarningsHistoryResponse {
  items: EarningsHistoryItem[];
  pagination: { total: number; limit: number; offset: number; has_next: boolean };
}

// ─── Idempotency-Key helpers ──────────────────────────────────────────────────
const IDEM_KEY = 'awardy:withdraw:idem-key';
const PENDING_ID_KEY = 'awardy:withdraw:pending-id';
function getOrCreateWithdrawKey(): string {
  let key = sessionStorage.getItem(IDEM_KEY);
  if (!key) { key = crypto.randomUUID(); sessionStorage.setItem(IDEM_KEY, key); }
  return key;
}
function rotateWithdrawKey(): void { sessionStorage.removeItem(IDEM_KEY); }
function clearWithdrawSession(): void {
  sessionStorage.removeItem(IDEM_KEY);
  sessionStorage.removeItem(PENDING_ID_KEY);
}
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function normalizeWithdrawStatus(status?: string | null): string | undefined {
  return status?.toUpperCase();
}

function mapWithdrawError(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const msg = err.message;
  if (msg.includes('API 402')) return 'Недостаточно средств';
  if (msg.includes('API 409')) return 'Попробуй ещё раз';
  if (msg.includes('API 429')) return 'Вывод уже обрабатывается. Подожди минуту';
  if (msg.includes('API 404')) return 'Вывод временно недоступен';
  return 'Произошла ошибка';
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function WalletSkeleton() {
  return (
    <div style={{ padding: '8px 16px 0' }}>
      <div style={{ borderRadius: 20, background: P.ink, padding: 20, border: 'none',
        boxShadow: '0 4px 24px rgba(25,23,23,0.28)' }}>
        <Skeleton h={13} w={130} style={{ marginBottom: 10, opacity: 0.3 }} />
        <Skeleton h={52} w={200} style={{ opacity: 0.22 }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 22 }}>
          <Skeleton h={50} style={{ flex: 1, borderRadius: 25, opacity: 0.18 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        {[0, 1].map((i) => (
          <div key={i} style={{ flex: 1, background: P.card, borderRadius: 20, padding: 14,
            border: `1px solid ${P.line}` }}>
            <Skeleton h={12} w={60} style={{ marginBottom: 8 }} />
            <Skeleton h={26} w={80} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── WalletScreen ─────────────────────────────────────────────────────────────
export default function WalletScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [withdrawalId, setWithdrawalId] = useState<string | null>(() =>
    sessionStorage.getItem(PENDING_ID_KEY)
  );
  const pollStartRef = useRef<number | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawFailReason, setWithdrawFailReason] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery<BalanceView>({
    queryKey: ['earnings', 'balance'],
    queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
    retry: false,
  });
  const { data: profile } = useQuery<ProfileView>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch<ProfileView>('/api/v2/buyer/profile'),
    retry: false,
  });
  const { data: pollData } = useQuery<WithdrawStatusResponse>({
    queryKey: ['withdrawal', withdrawalId],
    queryFn: () => apiFetch<WithdrawStatusResponse>(`/api/v2/buyer/earnings/withdraw/${withdrawalId}`),
    enabled: !!withdrawalId && !pollTimedOut,
    refetchInterval: (query) => {
      const pollStatus = normalizeWithdrawStatus(query.state.data?.status);
      if (pollStatus && TERMINAL_STATUSES.has(pollStatus)) return false;
      if (pollStartRef.current !== null && Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) return false;
      return 3000;
    },
    refetchOnWindowFocus: false,
  });
  const { data: history, isPending: historyPending, isError: historyError } =
    useQuery<EarningsHistoryResponse>({
      queryKey: ['earnings', 'history'],
      queryFn: () => apiFetch<EarningsHistoryResponse>('/api/v2/buyer/earnings/history?limit=20'),
      retry: false,
    });

  useEffect(() => {
    if (!pollData) return;
    const pollStatus = normalizeWithdrawStatus(pollData.status);
    const { reason } = pollData;
    if (pollStatus === 'COMPLETED') {
      void queryClient.invalidateQueries({ queryKey: ['earnings', 'balance'] });
      clearWithdrawSession(); setWithdrawalId(null); setWithdrawSuccess(true);
    } else if (pollStatus === 'FAILED' || pollStatus === 'CANCELLED') {
      setWithdrawFailReason(reason ?? 'Вывод не выполнен');
      clearWithdrawSession(); setWithdrawalId(null);
    }
  }, [pollData, queryClient]);

  useEffect(() => {
    if (!withdrawalId || pollTimedOut) return;
    if (pollStartRef.current === null) pollStartRef.current = Date.now();
    const timer = setTimeout(() => setPollTimedOut(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [withdrawalId, pollTimedOut]);

  const withdrawMutation = useMutation<WithdrawInitResponse, Error, void>({
    mutationFn: () => apiFetch<WithdrawInitResponse>('/api/v2/buyer/earnings/withdraw', {
      method: 'POST',
      headers: { 'Idempotency-Key': getOrCreateWithdrawKey() },
      body: JSON.stringify({ amount_kopecks: data!.available_kopecks }),
    }),
    onSuccess: (res) => {
      setWithdrawalId(res.withdrawal_id);
      sessionStorage.setItem(PENDING_ID_KEY, res.withdrawal_id);
      pollStartRef.current = Date.now();
      setWithdrawError(null); setWithdrawFailReason(null);
    },
    onError: (err) => {
      const msg = err.message;
      if (msg.includes('API 422') && msg.includes('SBP_PROFILE_INCOMPLETE')) { navigate('/requisites'); return; }
      if (msg.includes('API 409')) rotateWithdrawKey();
      setWithdrawError(mapWithdrawError(err));
    },
  });

  const sbpComplete = profile?.sbp_complete ?? false;
  const availableKopecks = data?.available_kopecks ?? 0;
  const isPolling = !!withdrawalId && !pollTimedOut;

  function handleWithdrawClick() {
    if (!sbpComplete) { navigate('/requisites'); return; }
    if (availableKopecks === 0) return;
    withdrawMutation.mutate();
  }
  function handleRetry() {
    rotateWithdrawKey(); setWithdrawFailReason(null); setWithdrawError(null);
    withdrawMutation.mutate();
  }
  const btnDisabled = sbpComplete && (availableKopecks === 0 || withdrawMutation.isPending || isPolling);

  return (
    <IOSScreen tabBarSpace>
      <PageHeader title="Копилка" />
      {isPending && <WalletSkeleton />}
      {isError && !isPending && <EmptyState title="Копилка недоступна" subtitle="Попробуй позже" />}

      {!isPending && !isError && data !== undefined && (
        <div style={{ padding: '8px 16px 0' }}>

          {/* ── Dark hero card ── */}
          <div style={{
            borderRadius: 20, background: P.ink, padding: 20, position: 'relative',
            overflow: 'hidden', border: 'none', boxShadow: '0 4px 24px rgba(25,23,23,0.28)',
          }}>
            {/* Caveat "копилка" annotation */}
            <span style={{
              position: 'absolute', top: 18, right: 18, fontFamily: T.fontAccent,
              fontWeight: 700, fontSize: 22, color: P.terraLight,
              transform: 'rotate(8deg)', display: 'inline-block', lineHeight: 1, opacity: 0.9,
            }}>копилка</span>

            <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.60)', margin: '0 0 4px', fontWeight: 500 }}>
              Доступно к выводу
            </p>
            <p style={{ font: T.heroAmount, color: '#FFFFFF', margin: 0, letterSpacing: -0.8 }}>
              {formatKopecks(data.available_kopecks)}
            </p>
            {data.pending_kopecks > 0 && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                +{formatKopecks(data.pending_kopecks)} ждут начисления
              </p>
            )}
            {withdrawSuccess && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0', fontWeight: 600 }}>
                Деньги отправлены
              </p>
            )}
            {pollTimedOut && !withdrawSuccess && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                Обрабатывается — уведомим в Telegram
              </p>
            )}
            {isPolling && !withdrawSuccess && (
              <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.55)', margin: '8px 0 0' }}>
                Обрабатываем…
              </p>
            )}
            {withdrawError && !withdrawFailReason && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0' }}>{withdrawError}</p>
            )}

            {/* Action buttons row */}
            <div style={{ display: 'flex', gap: 8, marginTop: 22 }}>
              {withdrawFailReason ? (
                <PressBtn bg={P.terracotta} fg="#fff" onClick={handleRetry}>Попробовать ещё раз</PressBtn>
              ) : (
                <>
                  <PressBtn bg="#ffffff" fg={P.ink} disabled={btnDisabled} onClick={handleWithdrawClick}>
                    {withdrawMutation.isPending ? 'Отправляем…' : 'Вывести'}
                  </PressBtn>
                  <PressBtn bg="rgba(255,255,255,0.12)" fg="#ffffff" full={false}
                    style={{ flexShrink: 0, paddingLeft: 20, paddingRight: 20 }}
                    onClick={() => navigate('/requisites')}>
                    Реквизиты
                  </PressBtn>
                </>
              )}
            </div>
            {withdrawFailReason && (
              <p style={{ font: T.footnote, color: P.terraLight, margin: '8px 0 0', textAlign: 'center' }}>
                {withdrawFailReason}
              </p>
            )}
          </div>

          {/* ── SBP requisites nudge ── */}
          {!sbpComplete && (
            <Card p={14} r={20} hover onClick={() => navigate('/requisites')} style={{
              marginTop: 12, background: '#FFF6E5', borderColor: 'rgba(212,137,14,0.35)',
              display: 'flex', gap: 12, alignItems: 'center',
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: P.warn,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Ic name="warning" size={17} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ font: T.headline, color: S.label, margin: 0 }}>Заполни реквизиты для вывода</p>
                <p style={{ font: T.footnote, color: S.secondaryLabel, margin: '2px 0 0' }}>Банк, ФИО и телефон СБП</p>
              </div>
              <Ic name="chevR" size={14} color={P.warn} />
            </Card>
          )}

          {/* ── Stats row ── */}
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {[
              { label: 'Начислено', val: data.accrued_total_kopecks },
              { label: 'Выведено', val: data.withdrawn_total_kopecks },
            ].map(({ label, val }) => (
              <div key={label} style={{ flex: 1 }}>
                <Card p={14} r={20}>
                  <p style={{ font: T.footnote, color: S.secondaryLabel, margin: '0 0 4px',
                    textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</p>
                  <p style={{ font: T.title2, color: S.label, margin: 0 }}>{formatKopecks(val)}</p>
                </Card>
              </div>
            ))}
          </div>

          <SectionHeader>История</SectionHeader>
          <HistorySection isPending={historyPending} isError={historyError} items={history?.items ?? []} />
        </div>
      )}
    </IOSScreen>
  );
}
